import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';

import { acquireApiToken, forceRefreshApiToken } from '@/auth/tokenProvider';
import { devWarn } from '@/utils/logging';

type RetriableConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

/**
 * Auth is pull-based: every request asks MSAL for a currently valid token at
 * send time, so a request can never go out with the stale snapshot that the
 * global default header may still hold after an idle/frozen tab. Both
 * interceptors are strictly best-effort — every failure path falls back to
 * the request exactly as it would have been sent without them.
 */
export const attachAuthInterceptors = (client: AxiosInstance): void => {
  client.interceptors.request.use(async (config) => {
    try {
      const token = await acquireApiToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      devWarn(
        'authInterceptors: token acquisition failed, sending request with existing header',
        error
      );
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      if (error.response?.status !== 401 || !config || config._authRetry) {
        return Promise.reject(error);
      }

      config._authRetry = true;
      try {
        const token = await forceRefreshApiToken();
        if (!token) {
          return Promise.reject(error);
        }
        config.headers.Authorization = `Bearer ${token}`;
      } catch (refreshError) {
        // Interaction-required and transient refresh failures surface as the
        // original 401; useTokenRefresh remains the only owner of interactive
        // login, so no popup/redirect is ever started from here.
        devWarn('authInterceptors: token refresh failed', refreshError);
        return Promise.reject(error);
      }
      return client(config);
    }
  );
};
